/**
 * Compact layout for the epi-study-agent chat — drops the full-page chrome
 * (session list, memory panel, side rails) so the chat fits inside a
 * fixed-width sidebar mount such as the global research-assistant slot.
 *
 * Two host modes, switched by ``onArtifactOpen``:
 *
 * - **Legacy / global-RA mount** (no ``onArtifactOpen``): the host has no
 *   route-driven detail surface, so clicking an artifact opens the
 *   right-anchored {@link ArtifactDrawer} over page content.
 * - **Scope-driven mount** (project page, ``onArtifactOpen`` provided):
 *   clicking an artifact hands off to the host, which threads the
 *   artifact's route through ``useChatPreservingNavigate`` so the
 *   right-pane outlet renders the same detail tabs the drawer used to
 *   show. The drawer is then not mounted at all — the host's
 *   scope-router IS the artifact viewer. Gen_3 only.
 */

import { Alert, Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { ArtifactDrawer } from './ArtifactDrawer';
import { ChatComposer, type ChatComposerScopeChip } from './ChatComposer';
import { KnowledgeBrowserDialog } from './KnowledgeBrowserDialog';
import { MessageList } from './MessageList';
import { PinnedClarifyingQuestion, REPLY_DIRECTLY_PLACEHOLDER } from './PinnedClarifyingQuestion';
import { QueuedMessagesStrip } from './QueuedMessagesStrip';
import { ReviewCard } from './ReviewCard';
import { ReviewSkippedCard } from './ReviewSkippedCard';
import { WelcomeMessage, type WelcomeVariant } from './WelcomeMessage';
import { AvSlugClickProvider } from '../context/AvSlugClickContext';
import { ConceptGroupNavProvider } from '../context/ConceptGroupNavContext';
import { KnownAvSlugsProvider } from '../context/KnownAvSlugsContext';
import { useChatAttachments } from '../hooks/useChatAttachments';
import { useConnectionAlertVisible } from '../hooks/useConnectionAlertVisible';
import type { UseEpiChatPageResult } from '../hooks/useEpiChatPage';
import { usePendingReviewItems } from '../hooks/usePendingReviewItems';
import { usePinnedClarifyingQuestion } from '../hooks/usePinnedClarifyingQuestion';
import { useReviewCard } from '../hooks/useReviewCard';
import type { EpiArtifact, EpiMessage } from '../types/epi-chat-types';
import { composerLoadingMessage } from '../utils/composerLoadingMessage';

export interface EpiChatPanelLayoutProps {
  page: UseEpiChatPageResult;
  /**
   * When the chat is mounted inside a scoped surface (the agent-first
   * project page), this prop is the current-scope indicator above the
   * input. Mounts without scope (legacy global RA, etc.) omit it.
   */
  scopeChip?: ChatComposerScopeChip | null;
  /**
   * Overrides the composer placeholder. Project page sets this to "Ask
   * about <name>…" when scoped; defaulting flows through ChatComposer.
   */
  composerPlaceholder?: string;
  /**
   * When provided, clicking an artifact bubble in chat hands off to the
   * host instead of opening the in-component drawer. The host typically
   * navigates the page to the artifact's detail route, where the
   * scope-driven right pane renders the equivalent tabs. Providing this
   * prop also suppresses the drawer entirely — the host's outlet is the
   * single artifact viewer. Project-page mount only; legacy mounts omit
   * this and get the drawer.
   * TODO: cleanup with research_assistant_gen_3 ff
   */
  onArtifactOpen?: (artifact: EpiArtifact) => void;
  /**
   * When provided, clicking an inline AV-slug link in a chat message
   * calls this instead of opening the knowledge-browser dialog. The
   * project-page mount injects a navigate fn that routes to the variable
   * detail route. Legacy mounts omit this and get the dialog fallback.
   */
  onAvSlugClick?: (slug: string) => void;
  /**
   * OR-6089: click handler for `resource_ref` cards in the chat stream.
   * Caller injects a navigate fn — typically
   * ``useChatPreservingNavigate`` from `feature-project-page` — so the
   * card click routes to a TanStack-driven detail page while
   * preserving the active chat. Legacy mounts that don't carry a host
   * route can omit it; the card then renders without click-through.
   */
  onResourceNavigate?: (route: string) => void;
  /** OR-6504: opens a variable GROUP's details page from the family card. */
  onOpenVariableGroup?: (familyId: string) => void;
  /**
   * OR-6126: click handler for concept-group links/codes in the chat stream.
   * When provided (project-page mount), clicking routes to the in-pane
   * concept-group artifact instead of opening the details modal; standalone
   * mounts omit it and keep the modal fallback.
   */
  onConceptGroupNavigate?: (conceptGroupId: string) => void;
  /**
   * OR-6125: concept-group file-upload handler. When provided (gen_3 +
   * concept-group-tools), the composer's attach button is enabled and the picked
   * file is handed back here for upload. Omitted → the attach button stays disabled.
   */
  onAttach?: (file: File) => void;
  /**
   * Alternative to {@link onAttach}: opens a handler (e.g. the create-concept-
   * group modal) directly instead of a native file picker. Takes precedence.
   */
  onAttachClick?: () => void;
  /**
   * Enables message attachments against this project. Hosts that pass it drop
   * {@link onAttachClick} — one button, one action.
   */
  attachmentUploadProjectId?: string;
  /**
   * OR-6123 (Batch A): when set, the chat's zero-message empty state
   * renders the static Research-Assistant welcome greeting for this
   * variant instead of the generic "Ask the agent…" fallback. The
   * project-page mount decides the variant ("first" vs "returning") from
   * the project's chats list; legacy/global-RA mounts omit it and keep
   * the generic fallback.
   * TODO: cleanup with research_assistant_gen_3 ff
   */
  welcomeVariant?: WelcomeVariant;
  /**
   * A brand-new chat's first message, rendered immediately from the
   * client-minted stash instead of the "Loading session…" spinner while the
   * chat row is created and the socket subscribes. See
   * ``MessageList``'s ``pendingOptimisticMessage`` prop.
   */
  pendingOptimisticMessage?: EpiMessage | null;
  /**
   * OR-6348: forces the "RA is working" indicator and Stop affordance on
   * during the same window ``pendingOptimisticMessage`` covers — a brand-new
   * chat's first send has no real run to key ``runState`` off yet (the chat
   * row is still being created and the socket is still subscribing), so
   * without this override the user sees their own bubble and nothing else
   * for however long that round-trip takes. Purely presentational: it does
   * NOT touch ``runState``/``runId``, so it can't interfere with the
   * busy-guard that queues a genuine double-send.
   */
  forceWorkingIndicator?: boolean;
  /**
   * OR-6455: renders without a composer and with every transcript write
   * affordance hard-disabled — an internal viewer looking at a chat they
   * don't own via the internal-chat-visibility capability. Folded into the
   * SAME `disabled` gate the subscribe-handshake window already uses
   * (rather than a second parallel disabled path), so
   * `PinnedClarifyingQuestion` and `MessageList`'s pending-question /
   * checkpoint-answer affordances are covered for free. `page.sendMessage`
   * etc. are expected to be no-ops for a read-only caller regardless —
   * this is belt-and-suspenders, not the only guard.
   */
  readOnly?: boolean;
  /**
   * Copy shown in place of the composer when `readOnly` is set. Defaults to
   * a generic message; callers with an owner label (e.g. "Viewing Jane's
   * chat") should pass a more specific one.
   */
  readOnlyBannerLabel?: string;
  /** Host-driven disable + inline alert; unlike `readOnly`, keeps the composer mounted. */
  composerBlockedMessage?: string | null;
  /**
   * OR-6455: overrides every user-turn avatar's initials — see
   * `MessageBubble`'s own doc for why. Only ever meaningful alongside
   * `readOnly`; every other caller omits it and keeps the logged-in
   * viewer's own initials.
   */
  userAvatarInitials?: string;
  'data-testid'?: string;
}

export const EpiChatPanelLayout = ({
  page,
  scopeChip,
  composerPlaceholder,
  onArtifactOpen,
  onAvSlugClick,
  onResourceNavigate,
  onOpenVariableGroup,
  onConceptGroupNavigate,
  onAttach,
  onAttachClick,
  attachmentUploadProjectId,
  welcomeVariant,
  pendingOptimisticMessage,
  forceWorkingIndicator,
  readOnly = false,
  readOnlyBannerLabel,
  composerBlockedMessage,
  userAvatarInitials,
  ...rest
}: EpiChatPanelLayoutProps) => {
  const {
    status,
    messages,
    queue,
    runState,
    error,
    initializing,
    subscribing,
    subscribeAcked,
    artifacts,
    activeArtifact,
    activeArtifactTab,
    activeSessionId,
    sendMessage,
    cancelRun,
    dequeueMessage,
    selectArtifact,
    selectArtifactTab,
    applyArtifactScope,
    interruptedRun,
    retryInterruptedRun,
  } = page;

  const [previewOpen, setPreviewOpen] = useState(false);
  const hostOpensArtifacts = onArtifactOpen != null;
  // No-ops when the host hasn't enabled attachments.
  const attachments = useChatAttachments(attachmentUploadProjectId);

  // Gates AV-slug link detection to slugs actually present in this
  // session's artifacts/checkpoints, so a pattern-only match in message
  // prose can't link to an unrelated variable that merely looks like a
  // slug. Also includes slugs from av_selection_checkpoint parts so that library
  // matches (searched-and-fetched analytical variables) are linkable in MessageBubble prose.
  const knownAvSlugs = useMemo(() => {
    const slugs = new Set<string>();
    for (const artifact of artifacts) {
      if (artifact.name) slugs.add(artifact.name);
      for (const source of artifact.sources ?? []) slugs.add(source);
      for (const entry of artifact.knowledgeApplied ?? []) {
        if (entry.kind !== 'av') continue;
        const slug = entry.id.startsWith('av.') ? entry.id.slice(3) : entry.source;
        if (slug) slugs.add(slug);
      }
    }
    for (const message of messages) {
      for (const part of message.parts) {
        if (part.kind !== 'av_selection_checkpoint') continue;
        if (part.checkpoint.primary?.name) slugs.add(part.checkpoint.primary.name);
        for (const alt of part.checkpoint.alternatives) {
          if (alt.name) slugs.add(alt.name);
        }
      }
    }
    return slugs;
  }, [artifacts, messages]);

  // ``disabled`` hard-gates BOTH the
  // transcript cards (pending question / checkpoint answer affordances,
  // artifact-ref click-through) AND the composer until the subscribe handshake
  // is acked — so a cached pending card painted on refresh can't emit a
  // ``user_message`` before the subscribe ack and earn a ``NOT_SUBSCRIBED``
  // error. ``notConnected`` is kept explicitly (a post-run socket drop can leave
  // ``subscribeAcked`` true while disconnected via the wedge backstops).
  const notConnected = status !== 'connected';
  const disabled =
    readOnly || notConnected || !subscribeAcked || initializing || Boolean(composerBlockedMessage);
  const loadingMessage = composerLoadingMessage(status, initializing, subscribeAcked);
  const showConnectionAlert = useConnectionAlertVisible(status);

  // Pinned clarify (research_assistant_pinned_clarify): the last message's
  // unanswered clarifying question renders attached to the composer instead
  // of as in-stream chips. TODO: cleanup with research_assistant_pinned_clarify ff
  const { pinnedQuestion, dismissPinnedQuestion } = usePinnedClarifyingQuestion(messages);

  // Review card (research_assistant_review_card): the turn's questions and
  // library matches in one card at the end of the chat. A question it covers
  // is not also pinned on its own. TODO: cleanup with research_assistant_review_card ff
  // ``awaiting_input`` is set the moment a card arrives, before the run's
  // RUN_FINISHED; only ``idle`` means the run is done.
  const agentRunning =
    runState === 'streaming' || runState === 'awaiting_input' || Boolean(forceWorkingIndicator);
  const { pendingItems: pendingReviewItems, visibleItems: reviewItems } = usePendingReviewItems(
    messages,
    agentRunning
  );
  const showReview = reviewItems.length > 0;
  // Held here, not inside the card: closing swaps the open card for a Skipped
  // card at the end of the transcript, and reopening must find the picks and
  // drafts intact.
  const reviewCard = useReviewCard({
    items: pendingReviewItems,
    onAnswer: (answer) => sendMessage(answer),
    disabled,
  });
  const reviewOpen = showReview && !reviewCard.isClosed;
  const pinnedClarifyQuestion =
    pinnedQuestion && !pendingReviewItems.some((item) => item.id === pinnedQuestion.questionId)
      ? pinnedQuestion
      : null;

  // Interrupted-run recovery (OR-5803): gates the Retry card shown when a pod
  // restart orphans an in-flight turn. TODO: cleanup with ra_graceful_shutdown_drain ff
  const interruptedRunRecoveryEnabled = useFeatureFlagSplit('ra_graceful_shutdown_drain');

  return (
    <ConceptGroupNavProvider value={{ navigateToConceptGroup: onConceptGroupNavigate }}>
      <AvSlugClickProvider onClick={onAvSlugClick ?? null}>
        <KnownAvSlugsProvider slugs={knownAvSlugs}>
          <Box
            data-testid={rest['data-testid']}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              backgroundColor: 'background.default',
            }}
          >
            {error && (
              <Alert severity="error" sx={{ margin: 1 }}>
                {error}
              </Alert>
            )}
            {showConnectionAlert && (
              <Alert severity="info" sx={{ margin: 1 }}>
                {status === 'connecting'
                  ? 'Connecting to the Research Assistant…'
                  : 'Not connected to the Research Assistant.'}
              </Alert>
            )}
            {!readOnly && composerBlockedMessage && (
              <Alert severity="info" sx={{ margin: 1 }} data-testid="epi-chat-composer-blocked">
                {composerBlockedMessage}
              </Alert>
            )}
            <MessageList
              messages={messages}
              onAnswerQuestion={(answer) => sendMessage(answer)}
              onSelectArtifact={(id) => {
                if (hostOpensArtifacts) {
                  // Scope-driven mount: hand off to the host, which routes the
                  // page to the artifact's detail surface. We deliberately do
                  // NOT update ``activeArtifactId`` here — there's no drawer
                  // listening, and route URL is the source of truth for what
                  // the right pane renders.
                  const artifact = artifacts.find((entry) => entry.id === id);
                  if (artifact) onArtifactOpen(artifact);
                  return;
                }
                selectArtifact(id);
                setPreviewOpen(true);
              }}
              onNavigateResource={onResourceNavigate}
              onOpenVariableGroup={onOpenVariableGroup}
              sessionId={activeSessionId}
              emptyState={welcomeVariant ? <WelcomeMessage variant={welcomeVariant} /> : undefined}
              // Same greeting, kept at the top of the thread once the first
              // turn lands (and on replay). Client-side only — never enters
              // ``messages``, so it's spliced from the agent's history.
              leadingElement={
                welcomeVariant ? <WelcomeMessage variant={welcomeVariant} /> : undefined
              }
              disabled={disabled}
              agentWorking={runState === 'streaming' || forceWorkingIndicator}
              sessionLoading={subscribing}
              pendingOptimisticMessage={pendingOptimisticMessage}
              userAvatarInitials={userAvatarInitials}
              trailingElement={
                showReview ? (
                  reviewCard.isClosed ? (
                    <ReviewSkippedCard
                      title={reviewCard.title}
                      headline={reviewCard.closedHeadline}
                      body={reviewCard.closedBody}
                      reopenLabel={reviewCard.reopenLabel}
                      disabled={reviewCard.disabled}
                      onReopen={reviewCard.onReopen}
                    />
                  ) : (
                    <ReviewCard card={reviewCard} />
                  )
                ) : undefined
              }
              interruptedRun={interruptedRunRecoveryEnabled ? interruptedRun : null}
              onRetryInterruptedRun={
                interruptedRunRecoveryEnabled ? retryInterruptedRun : undefined
              }
            />
            <Box
              sx={{
                // 24px chat gutter (OR-6123) — matches the message list + page.
                paddingX: 6,
                paddingY: 3,
                backgroundColor: 'background.paper',
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <QueuedMessagesStrip queue={queue} onDequeue={dequeueMessage} />
              {pinnedClarifyQuestion && (
                <PinnedClarifyingQuestion
                  question={pinnedClarifyQuestion}
                  onAnswer={(answer) => sendMessage(answer)}
                  onDismiss={dismissPinnedQuestion}
                  disabled={disabled}
                />
              )}
              {/* Hard-disabled until the subscribe handshake is acked (and
                  while disconnected / initializing): submit is blocked, not
                  queued, so a click can't reach the server before the subscribe
                  ack and earn a ``NOT_SUBSCRIBED`` error. The Stop button
                  (streaming branch) is not gated by ``disabled``, so an
                  in-flight run stays cancellable. */}
              {readOnly ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingY: 1.5,
                  }}
                  data-testid="epi-chat-readonly-banner"
                >
                  <Typography variant="body2" color="text.secondary">
                    {readOnlyBannerLabel ??
                      'Viewing — read only. Only the owner can send messages here.'}
                  </Typography>
                </Box>
              ) : (
                <ChatComposer
                  runState={runState}
                  forceStreaming={forceWorkingIndicator}
                  onSend={(text) => {
                    const staged = attachments.sendableAttachments;
                    sendMessage({ text, ...(staged.length ? { attachments: staged } : {}) });
                    // The references live on the sent message now.
                    attachments.clear();
                  }}
                  onCancel={cancelRun}
                  onAttach={attachmentUploadProjectId ? attachments.attach : onAttach}
                  attachments={attachmentUploadProjectId ? attachments.staged : undefined}
                  onRemoveAttachment={attachments.remove}
                  disabled={disabled}
                  onAttachClick={onAttachClick}
                  loadingMessage={loadingMessage}
                  placeholder={
                    pinnedClarifyQuestion || reviewOpen
                      ? REPLY_DIRECTLY_PLACEHOLDER
                      : composerPlaceholder
                  }
                  scopeChip={scopeChip}
                />
              )}
              {/* Read-only viewers (OR-6455) have no composer to double-check
                  output from — dropping this line gives that vertical space
                  back rather than leaving a disclaimer about an input action
                  that isn't available here. */}
              {!readOnly && (
                // The `caption` variant's line-height (266% of its font-size)
                // is tuned for multi-line reading and reserves a lot of
                // invisible half-leading above/below the glyphs — for this
                // single line it made the padding-based gaps above/below read
                // as much bigger than the divider-to-box gap. Tightened to a
                // single-line ratio so the explicit paddingTop below actually
                // matches that gap.
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', paddingX: 0.5, lineHeight: 1.4, paddingTop: 3 }}
                >
                  Research Assistant may make mistakes. Double-check all output.
                </Typography>
              )}
            </Box>
          </Box>

          {!hostOpensArtifacts && (
            <ArtifactDrawer
              artifact={activeArtifact}
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              activeTab={activeArtifactTab}
              onTabChange={selectArtifactTab}
              onArtifactScopeChange={applyArtifactScope}
            />
          )}

          {!hostOpensArtifacts && (
            <KnownAvSlugsProvider slugs={null}>
              <KnowledgeBrowserDialog activeSessionId={activeSessionId} />
            </KnownAvSlugsProvider>
          )}
        </KnownAvSlugsProvider>
      </AvSlugClickProvider>
    </ConceptGroupNavProvider>
  );
};
